import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { Container, ContainerContent } from 'components/Containers/Containers'
import { Button, Flex, Tabs as PillTab, TabsContainer, colors } from 'styling/GlobalStyles'
import { BodyText, OtherText } from 'styling/Typography'
import { IKitchenMenuCell, IKitchenMenuTemplate, IKitchenTemplateEditForm } from 'types/kitchen/kitchenMenu.interface'
import { IKitchenMenuPlannerTabProps } from 'types/kitchen/kitchenTabProps.interface'
import {
  KITCHEN_STATUS_COLORS,
  KITCHEN_TAG_SETS,
  KITCHEN_WINDOWS,
  buildKitchenMenuDays,
  kitchenDemandFor,
  kitchenInitialAssignMock,
  kitchenInitialStatusMock,
  kitchenPastMenuMock,
  kitchenTemplatesMock,
} from '../kitchenData'
import {
  MenuGridCell,
  MenuLegendItem,
  MenuScheduleTable,
  MenuStatusDot,
  MenuTableHeadCell,
  MenuTableRowLabel,
} from '../Kitchen.styled'
import { MenuCellModal } from '../modals/MenuCellModal'
import { MenuTemplatesModal } from '../modals/MenuTemplatesModal'
import { TemplateEditorModal } from '../modals/TemplateEditorModal'
import { InstantiateTemplateModal } from '../modals/InstantiateTemplateModal'

const MOCK_WEEK_START = new Date(2028, 1, 16)

export const MenuPlannerTab = ({ kitchens, dishes, cookable, onToolbarChange }: IKitchenMenuPlannerTabProps) => {
  const [kitchenId, setKitchenId] = useState(1)
  const [weekStart, setWeekStart] = useState(MOCK_WEEK_START)
  const [assign, setAssign] = useState<{ [key: string]: number }>(kitchenInitialAssignMock)
  const [status, setStatus] = useState<{ [key: string]: string }>(kitchenInitialStatusMock)
  const [cell, setCell] = useState<IKitchenMenuCell | null>(null)
  const [templates, setTemplates] = useState<{ [id: number]: IKitchenMenuTemplate }>(kitchenTemplatesMock)
  const [mgrOpen, setMgrOpen] = useState(false)
  const [tplEdit, setTplEdit] = useState<IKitchenTemplateEditForm | null>(null)
  const [tplOpen, setTplOpen] = useState(false)
  const [tplSel, setTplSel] = useState(kitchenId === 1 ? 1 : 2)
  const [tplStart, setTplStart] = useState('21/2')

  const kitchen = kitchens.find((k) => k.id === kitchenId)!
  const tagSets = KITCHEN_TAG_SETS[kitchenId]
  const days = useMemo(() => buildKitchenMenuDays(weekStart), [weekStart])

  const dish = (id: number) => dishes.find((d) => d.id === id)

  const blankCycle = (len: number, tags: string[]) =>
    KITCHEN_WINDOWS.reduce(
      (c, w) => ({
        ...c,
        [w]: tags.reduce((m, t) => ({ ...m, [t]: Array(len).fill(null) }), {}),
      }),
      {},
    )

  const openNewTemplate = () => {
    const ts = KITCHEN_TAG_SETS[kitchenId]
    setTplEdit({ name: '', length: 14, tagSets: ts, cycle: blankCycle(14, ts) })
    setMgrOpen(false)
  }

  const openEditTemplate = (t: IKitchenMenuTemplate) => {
    const ts = Object.keys(t.cycle[KITCHEN_WINDOWS[0]] || {})
    setTplEdit(JSON.parse(JSON.stringify({ ...t, tagSets: ts })))
    setMgrOpen(false)
  }

  const saveTemplate = (form: IKitchenTemplateEditForm) => {
    const id = form.id || Math.max(...Object.keys(templates).map(Number), 0) + 1
    setTemplates({ ...templates, [id]: { id, name: form.name.trim(), length: form.length, cycle: form.cycle } })
    toast.success(`Template "${form.name.trim()}" saved (${form.length}-day cycle) — available in From template`)
    setTplEdit(null)
  }

  const tplPlan = () => {
    const tpl = templates[tplSel]
    if (!tpl) return { fills: [] as { key: string; dishId: number; day: string; w: string }[], skipped: 0 }
    const startIdx = days.findIndex((d) => d.d === tplStart)
    if (startIdx < 0) return { fills: [], skipped: 0 }
    const fills: { key: string; dishId: number; day: string; w: string }[] = []
    let skipped = 0
    days.forEach((d, di) => {
      if (di < startIdx || d.kind === 'past') return
      const cycleDay = (di - startIdx) % tpl.length
      KITCHEN_WINDOWS.forEach((w) => {
        if (status[`${kitchenId}-${d.d}-${w}`] === 'Published') {
          skipped++
          return
        }
        tagSets.forEach((t) => {
          const key = `${kitchenId}-${d.d}-${w}-${t}`
          if (assign[key]) {
            skipped++
            return
          }
          const dishId = tpl.cycle[w]?.[t]?.[cycleDay]
          if (dishId) fills.push({ key, dishId, day: d.d, w })
        })
      })
    })
    return { fills, skipped }
  }

  const applyTemplate = () => {
    const { fills, skipped } = tplPlan()
    const nextAssign = { ...assign }
    const nextStatus = { ...status }
    fills.forEach((f) => {
      nextAssign[f.key] = f.dishId
      const sk = `${kitchenId}-${f.day}-${f.w}`
      if (nextStatus[sk] !== 'Published') nextStatus[sk] = 'Draft'
    })
    setAssign(nextAssign)
    setStatus(nextStatus)
    setTplOpen(false)
    toast.success(
      `${templates[tplSel].name} instantiated from ${tplStart}: ${fills.length} slots filled as DRAFT${skipped ? `, ${skipped} skipped` : ''}`,
    )
  }

  const cellStatus = (day: (typeof days)[0], window: string) => {
    if (day.kind === 'past') return 'history'
    const st = status[`${kitchenId}-${day.d}-${window}`]
    const keys = tagSets.map((t) => `${kitchenId}-${day.d}-${window}-${t}`)
    const assigned = keys.filter((k) => assign[k]).length
    if (st === 'Published') return 'published'
    if (assigned === 0) return 'unassigned'
    const short = keys.some((k, i) => {
      const id = assign[k]
      if (!id) return false
      const c = cookable(dish(id)!)
      return c !== null && c < kitchenDemandFor(window, tagSets[i])
    })
    if (short) return 'shortfall'
    return assigned < keys.length ? 'partial' : 'draft'
  }

  const dotColor = (st: string) => KITCHEN_STATUS_COLORS[st] || KITCHEN_STATUS_COLORS.history

  const cellDishes = (day: (typeof days)[0], window: string) =>
    day.kind === 'past'
      ? (kitchenPastMenuMock[window] || []).map((id) => dish(id)?.name || '')
      : tagSets
        .map((t) => assign[`${kitchenId}-${day.d}-${window}-${t}`])
        .filter(Boolean)
        .map((id) => dish(id!)?.name || '')

  const handlePublish = () => {
    if (!cell) return
    const w = cell.window
    const d = cell.day
    setStatus((s) => ({ ...s, [`${kitchenId}-${d.d}-${w}`]: 'Published' }))
    toast.success(`${w} ${d.d} published — coverage ✓ dietary ✓ normatives ✓ · visible in Food Logistics`)
    setCell(null)
  }

  const handleWeekChange = useCallback((firstDay: Date) => {
    setWeekStart(firstDay)
  }, [])

  const { fills, skipped } = tplPlan()

  useEffect(() => {
    onToolbarChange?.(
      <Flex gap='1rem' alignEnd wrap>
        <TabsContainer>
          {kitchens.map((k) => (
            <PillTab key={k.id} active={kitchenId === k.id} onClick={() => { setKitchenId(k.id); setTplSel(k.id === 1 ? 1 : 2) }}>
              {k.name}
            </PillTab>
          ))}
        </TabsContainer>
        <CalendarInput
          week
          width='16rem'
          labelText='Week'
          defaultValue={MOCK_WEEK_START}
          onChange={handleWeekChange as any}
          nomarg
        />
        <Button orangeBorder onClick={() => { setTplSel(kitchenId === 1 ? 1 : 2); setTplOpen(true) }}>
          From template
        </Button>
        <Button orangeBorder onClick={() => setMgrOpen(true)}>Templates</Button>
        <Button orangeBorder onClick={() => toast.success('Weekly menu exported for mess hall display')}>
          Weekly menu
        </Button>
      </Flex>,
    )
    return () => onToolbarChange?.(null)
  }, [kitchens, kitchenId, onToolbarChange, handleWeekChange, setTplSel, setMgrOpen, setTplOpen])

  return (
    <Flex column gap='1rem' width='100%'>
      <Container>
        <ContainerContent>
          <MenuScheduleTable>
            <thead>
              <tr>
                <MenuTableHeadCell $corner />
                {days.map((d) => (
                  <MenuTableHeadCell key={d.d}>
                    {d.kind === 'today' ? (
                      <BodyText weight='700' style={{ color: colors.orange }}>
                        {d.label}
                      </BodyText>
                    ) : (
                      <OtherText grey>{d.label}</OtherText>
                    )}
                  </MenuTableHeadCell>
                ))}
              </tr>
            </thead>
            <tbody>
              {KITCHEN_WINDOWS.map((w) => (
                <tr key={w}>
                  <MenuTableRowLabel>
                    <BodyText weight='700'>{w}</BodyText>
                  </MenuTableRowLabel>
                  {days.map((d) => {
                    const st = cellStatus(d, w)
                    const names = cellDishes(d, w)
                    return (
                      <MenuGridCell
                        key={d.d}
                        $today={d.kind === 'today'}
                        $past={d.kind === 'past'}
                        onClick={() => setCell({ day: d, window: w })}
                      >
                        <Flex gap='0.375rem' center mb='4'>
                          <MenuStatusDot $color={dotColor(st)} />
                          <OtherText grey style={{ fontSize: '0.65625rem', textTransform: 'uppercase' }}>
                            {st}
                          </OtherText>
                        </Flex>
                        {names.length ? (
                          names.map((n) => (
                            <BodyText key={n} style={{ fontSize: '0.71875rem', lineHeight: 1.5 }}>
                              {n}
                            </BodyText>
                          ))
                        ) : (
                          <OtherText grey style={{ fontSize: '0.71875rem' }}>
                            —
                          </OtherText>
                        )}
                      </MenuGridCell>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </MenuScheduleTable>
          <Flex gap='1rem' mt='10' wrap>
            {[
              ['published', KITCHEN_STATUS_COLORS.published],
              ['draft / partial', KITCHEN_STATUS_COLORS.draft],
              ['unassigned', KITCHEN_STATUS_COLORS.unassigned],
              ['shortfall', KITCHEN_STATUS_COLORS.shortfall],
              ['history (read-only)', KITCHEN_STATUS_COLORS.history],
            ].map(([l, c]) => (
              <MenuLegendItem key={l}>
                <MenuStatusDot $color={c as string} />
                <OtherText grey>{l}</OtherText>
              </MenuLegendItem>
            ))}
          </Flex>
        </ContainerContent>
      </Container>

      {mgrOpen && !tplEdit && (
        <MenuTemplatesModal
          templates={templates}
          closeModal={() => setMgrOpen(false)}
          onEdit={openEditTemplate}
          onNew={openNewTemplate}
        />
      )}
      {tplEdit && (
        <TemplateEditorModal
          form={tplEdit}
          dishes={dishes}
          closeModal={() => setTplEdit(null)}
          onSave={saveTemplate}
        />
      )}
      {tplOpen && (
        <InstantiateTemplateModal
          templates={templates}
          tplSel={tplSel}
          setTplSel={setTplSel}
          tplStart={tplStart}
          setTplStart={setTplStart}
          fills={fills}
          skipped={skipped}
          kitchen={kitchen}
          days={days}
          dishes={dishes}
          closeModal={() => setTplOpen(false)}
          onApply={applyTemplate}
        />
      )}
      {cell && (
        <MenuCellModal
          cell={cell}
          kitchen={kitchen}
          tagSets={tagSets}
          dishes={dishes}
          assign={assign}
          setAssign={setAssign}
          status={status}
          pastMenu={kitchenPastMenuMock}
          cookable={cookable}
          closeModal={() => setCell(null)}
          onPublish={handlePublish}
        />
      )}
    </Flex>
  )
}
